/**
 * A resposta do agente como **célula de notebook** (`W-14`, ADR-049).
 *
 * Cada ficha da resposta é uma célula: o título, as linhas que resumem (o total, o maior, o
 * menor — as mesmas que o modelo leu e a conferência aceitou), o gráfico **e** a tabela de
 * onde ele saiu — ordenável, para baixar em planilha —, e o "como calculei". No computador, a
 * célula abre em **tela cheia**: o gráfico grande e a tabela ao lado. No celular, as duas
 * vistas se alternam numa chave, e o método fica a um toque.
 *
 * A tela não calcula nada: a tabela chega com o valor cru, e a grafia é a de `celula.ts`, a
 * mesma do servidor. O link da ficha só vale se for um caminho do próprio cockpit.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type {
  FichaDoAgente,
  RespostaDoAgente,
  TabelaDaFicha,
} from "../api/tipos";
import {
  baixarCsv,
  eNumerica,
  formatarCelula,
  ordenarLinhas,
  type Direcao,
} from "./celula";
import { GraficoDoAgente } from "./GraficoDoAgente";
import { Icone } from "./icones";

/** Só caminho do cockpit vira link: "/senatran?…", nunca "//outro" nem "javascript:". */
function caminhoInterno(link: string | null | undefined): string | null {
  return link && link.startsWith("/") && !link.startsWith("//") ? link : null;
}

// ------------------------------------------------------------------------- a resposta

export function RespostaNaTela({
  resposta,
  aoAbrir,
  aoContinuar,
}: {
  resposta: RespostaDoAgente;
  aoAbrir?: () => void;
  /** Só a última resposta oferece continuações: as de antes já ficaram para trás. */
  aoContinuar?: ((pergunta: string) => void) | undefined;
}) {
  return (
    <article className="grid min-w-0 grid-cols-1 gap-1.5">
      {resposta.texto && (
        <p className="rounded-cartao border border-linha bg-painel px-2.5 py-2 text-[12.5px] whitespace-pre-line text-tinta">
          {resposta.texto}
        </p>
      )}
      {resposta.fichas.map((ficha, i) => (
        <Celula key={i} ficha={ficha} aoAbrir={aoAbrir} />
      ))}
      {resposta.avisos.map((aviso) => (
        <p
          key={aviso}
          className="rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2 py-1 text-[11px] text-amarelo"
        >
          {aviso}
        </p>
      ))}
      <span className="text-[10.5px] text-tinta-3">
        {resposta.entendida_por === "modelo"
          ? "Entendida pelo modelo de linguagem · números conferidos nas consultas"
          : "Entendida pelas regras (sem modelo)"}
      </span>
      {aoContinuar && resposta.continuacoes.length > 0 && (
        <ul
          aria-label="Continuar a conversa"
          className="flex flex-wrap gap-1.5 pt-0.5"
        >
          {resposta.continuacoes.map((pergunta) => (
            <li key={pergunta}>
              <button
                type="button"
                onClick={() => aoContinuar(pergunta)}
                className="inline-flex min-h-8 items-center gap-1 rounded-pilula border border-azul-borda bg-azul-fundo px-2.5 py-1 text-left text-[11.5px] text-azul-claro hover:border-azul"
              >
                <Icone.Seta />
                {pergunta}
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

// ------------------------------------------------------------------------- a célula

type Vista = "grafico" | "tabela";

/** A altura do gráfico na tela cheia: o mesmo desenho, com espaço. */
const ALTURA_AMPLA = 320;

export function Celula({
  ficha,
  aoAbrir,
  amplo = false,
}: {
  ficha: FichaDoAgente;
  aoAbrir?: (() => void) | undefined;
  /** A célula em tela cheia: gráfico grande e tabela ao lado, método aberto. */
  amplo?: boolean;
}) {
  const grafico = ficha.grafico ?? null;
  const tabela = ficha.tabela ?? null;
  const [vista, definirVista] = useState<Vista>(grafico ? "grafico" : "tabela");
  const [cheia, definirCheia] = useState(false);
  const link = caminhoInterno(ficha.link);
  // O ranking já mostra cada item com o valor ao lado: as linhas repetiriam o gráfico, e
  // ficam a um toque, para quem quer o número exato. Com tabela, ela é o número exato.
  const linhasRecolhidas = grafico?.tipo === "ranking" && !tabela;
  const linhas = (
    <ul className="grid gap-0.5">
      {ficha.linhas.map((linha, i) => (
        <li key={i} className="text-[11.5px] text-tinta-2">
          {linha}
        </li>
      ))}
    </ul>
  );
  const desenho = grafico && (
    <GraficoDoAgente
      grafico={grafico}
      aoAbrir={aoAbrir}
      tituloRepetido={grafico.titulo === ficha.titulo}
      {...(amplo ? { altura: ALTURA_AMPLA } : {})}
    />
  );
  const tabelaNaTela = tabela && (
    <TabelaDaCelula tabela={tabela} ampla={amplo} />
  );
  const ampliavel = !amplo && (grafico || tabela);

  return (
    <section
      aria-label={ficha.titulo}
      className={`grid min-w-0 grid-cols-1 gap-1.5 rounded-cartao border border-linha bg-painel-2 ${amplo ? "p-4" : "px-2.5 py-2"}`}
    >
      <header className="flex items-start gap-2">
        <h3
          className={`min-w-0 flex-1 font-bold text-tinta ${amplo ? "text-[15px]" : "text-[12px]"}`}
        >
          {ficha.titulo}
        </h3>
        {tabela && (
          <button
            type="button"
            onClick={() => baixarCsv(tabela, ficha.titulo)}
            title="Baixar a tabela em planilha (CSV, abre no Excel)"
            className="inline-flex min-h-7 shrink-0 items-center gap-1 rounded-botao border border-linha px-1.5 text-[10.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            <Icone.Baixar />
            CSV
          </button>
        )}
        {ampliavel && (
          // A tela cheia é do computador: no celular, a célula já ocupa a tela.
          <button
            type="button"
            onClick={() => definirCheia(true)}
            title="Ver em tela cheia"
            aria-label={`Ver “${ficha.titulo}” em tela cheia`}
            className="hidden min-h-7 shrink-0 items-center gap-1 rounded-botao border border-linha px-1.5 text-[10.5px] font-semibold text-tinta-2 hover:bg-realce md:inline-flex"
          >
            <Icone.Expandir />
            Ampliar
          </button>
        )}
      </header>

      {amplo ? (
        // Tela cheia: o gráfico grande e a tabela ao lado, sem chave.
        <div
          className={`grid min-w-0 grid-cols-1 items-start gap-3 ${desenho && tabelaNaTela ? "lg:grid-cols-[3fr_2fr]" : ""}`}
        >
          {desenho}
          {tabelaNaTela}
        </div>
      ) : (
        <>
          {grafico && tabela && (
            <div
              role="tablist"
              aria-label="Como ver o resultado"
              className="inline-flex justify-self-start rounded-botao border border-linha bg-painel p-0.5"
            >
              {(
                [
                  ["grafico", "Gráfico"],
                  ["tabela", `Tabela · ${tabela.total_de_linhas}`],
                ] as const
              ).map(([chave, rotulo]) => (
                <button
                  key={chave}
                  type="button"
                  role="tab"
                  aria-selected={vista === chave}
                  onClick={() => definirVista(chave)}
                  className={`min-h-7 rounded-[4px] px-2.5 text-[11px] font-semibold ${
                    vista === chave
                      ? "bg-realce text-tinta"
                      : "text-tinta-3 hover:text-tinta-2"
                  }`}
                >
                  {rotulo}
                </button>
              ))}
            </div>
          )}
          {vista === "grafico" || !tabela ? desenho : tabelaNaTela}
        </>
      )}

      {linhasRecolhidas ? (
        <details className="group">
          <summary className="cursor-pointer text-[11px] text-tinta-3 select-none hover:text-tinta-2">
            Os números exatos
          </summary>
          <div className="pt-1">{linhas}</div>
        </details>
      ) : (
        linhas
      )}

      {ficha.metodo && (
        <details open={amplo} className="group">
          <summary className="cursor-pointer text-[11px] text-tinta-3 select-none hover:text-tinta-2">
            Como calculei
          </summary>
          <p className="pt-1 text-[11px] leading-relaxed text-tinta-2">
            {ficha.metodo}
          </p>
        </details>
      )}

      {link && (
        <Link
          to={link}
          onClick={aoAbrir}
          className="justify-self-end rounded-botao border border-azul-borda px-2 py-0.5 text-[11px] font-semibold text-azul-claro hover:bg-azul-fundo"
        >
          {ficha.rotulo_do_link ?? "Abrir"} →
        </Link>
      )}

      {cheia && (
        <TelaCheia titulo={ficha.titulo} aoFechar={() => definirCheia(false)}>
          <Celula ficha={ficha} aoAbrir={aoAbrir} amplo />
        </TelaCheia>
      )}
    </section>
  );
}

function TelaCheia({
  titulo,
  aoFechar,
  children,
}: {
  titulo: string;
  aoFechar: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    // Na captura, e parando aí: o Esc fecha a tela cheia, não a conversa inteira.
    function aoTeclar(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      aoFechar();
    }
    window.addEventListener("keydown", aoTeclar, { capture: true });
    return () =>
      window.removeEventListener("keydown", aoTeclar, { capture: true });
  }, [aoFechar]);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${titulo} — tela cheia`}
      className="fixed inset-0 z-50 overflow-y-auto bg-fundo/95 px-6 py-5"
    >
      <div className="mx-auto grid max-w-6xl gap-3">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={aoFechar}
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            <Icone.Fechar />
            Fechar a tela cheia
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- a tabela

/** Linhas desenhadas de cada vez: duas mil linhas de tabela travam o celular, e quase
 *  ninguém rola até a duzentésima. A planilha leva todas. */
const LINHAS_POR_VEZ = 200;

export function TabelaDaCelula({
  tabela,
  ampla = false,
}: {
  tabela: TabelaDaFicha;
  ampla?: boolean;
}) {
  const [coluna, definirColuna] = useState<number | null>(null);
  const [direcao, definirDirecao] = useState<Direcao>("desc");
  const [todas, definirTodas] = useState(false);
  const ordenadas = ordenarLinhas(tabela.linhas, coluna, direcao);
  const visiveis = todas ? ordenadas : ordenadas.slice(0, LINHAS_POR_VEZ);
  const cortada = tabela.total_de_linhas > tabela.linhas.length;

  // Um toque ordena (o número do maior para o menor; o texto de A a Z), o segundo inverte,
  // o terceiro volta à ordem em que veio — a natural: hora, dia, mês.
  function alternar(i: number) {
    const numerica = eNumerica(tabela.colunas[i]!.tipo);
    const primeira: Direcao = numerica ? "desc" : "asc";
    if (coluna !== i) {
      definirColuna(i);
      definirDirecao(primeira);
    } else if (direcao === primeira) {
      definirDirecao(primeira === "desc" ? "asc" : "desc");
    } else {
      definirColuna(null);
    }
  }

  return (
    <div className="grid min-w-0 grid-cols-1 gap-1">
      <div
        className={`min-w-0 overflow-auto rounded-botao border border-linha-suave bg-painel ${ampla ? "max-h-[60vh]" : "max-h-[280px]"}`}
      >
        <table className="w-full border-collapse text-[11.5px]">
          <thead className="sticky top-0 z-[1] bg-painel">
            <tr>
              {tabela.colunas.map((c, i) => {
                const numerica = eNumerica(c.tipo);
                const ativa = coluna === i;
                return (
                  <th
                    key={c.chave}
                    scope="col"
                    aria-sort={
                      ativa
                        ? direcao === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                    className={`border-b border-linha px-2 py-1 font-semibold whitespace-nowrap text-tinta-2 ${numerica ? "text-right" : "text-left"}`}
                  >
                    <button
                      type="button"
                      onClick={() => alternar(i)}
                      className="inline-flex items-center gap-1 hover:text-tinta"
                      title="Ordenar por esta coluna"
                    >
                      {c.rotulo}
                      <span aria-hidden="true" className="text-tinta-3">
                        {ativa ? (direcao === "asc" ? "↑" : "↓") : "↕"}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visiveis.map((linha, i) => (
              <tr key={i} className="border-t border-linha-suave">
                {linha.map((valor, j) => {
                  const tipo = tabela.colunas[j]!.tipo;
                  return (
                    <td
                      key={j}
                      className={`px-2 py-1 ${
                        eNumerica(tipo)
                          ? "mono text-right whitespace-nowrap text-tinta"
                          : tipo === "texto"
                            ? "min-w-[9ch] text-tinta-2"
                            : "whitespace-nowrap text-tinta-2"
                      }`}
                    >
                      {formatarCelula(valor, tipo)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="flex flex-wrap items-center gap-x-2 text-[10.5px] text-tinta-3">
        <span>
          {cortada
            ? `${tabela.linhas.length.toLocaleString("pt-BR")} de ${tabela.total_de_linhas.toLocaleString("pt-BR")} linhas — a resposta parou no teto; para tudo, use a exportação.`
            : `${tabela.linhas.length.toLocaleString("pt-BR")} linha(s)`}
        </span>
        {!todas && ordenadas.length > LINHAS_POR_VEZ && (
          <button
            type="button"
            onClick={() => definirTodas(true)}
            className="font-semibold text-azul-claro hover:underline"
          >
            Mostrar todas
          </button>
        )}
      </p>
    </div>
  );
}
